import {
  IProjectUser,
  IProjectView,
  ISecurityGroup,
  ISubscriber,
  IUser,
} from '../../api-client/autogenerated';
import { isProjectArchived } from '../../scripts/permission-utils';
// name-utils rather than scripts/utils on purpose: this module is imported by pure-logic tests
// that do not stub the API client, and scripts/utils pulls it in through a circular module graph.
// See the note at the top of scripts/name-utils.ts.
import { sortLastNamesDesc } from '../../scripts/name-utils';

export type DirtyFieldsType =
  | boolean
  | null
  | {
      [key: string]: DirtyFieldsType;
    }
  | DirtyFieldsType[];

function typedKeys<T extends object>(obj: T) {
  return Object.keys(obj) as Array<keyof T>;
}

function isTrulyEmpty(value: unknown): boolean {
  if (value === undefined || value === '' || value === null) return true;
  if (typeof value === 'boolean') return false; // keep false
  if (typeof value === 'number') return false; // keep 0

  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    // If every item is empty (recursively)
    return value.every(isTrulyEmpty);
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return true;
    return entries.every(([_, v]) => isTrulyEmpty(v));
  }

  return false;
}

export function getDirtyOrDefinedValues<T extends Record<string, any>>(
  dirtyFields: Partial<Record<keyof T, DirtyFieldsType>>,
  values: T,
): Partial<T> {
  const result: Partial<T> = {};

  for (const key of typedKeys(values)) {
    const dirty = dirtyFields[key];
    const value = values[key];

    // Skip undefined or empty string if not dirty
    if (value === undefined || value === '') {
      if (dirty) result[key] = value as T[typeof key];
      continue;
    }

    // Arrays
    if (Array.isArray(value)) {
      if (Array.isArray(dirty)) {
        const filtered = (value as any[])
          .map((v, i) => {
            const childDirty = dirty[i];
            if (childDirty && typeof childDirty === 'object' && !Array.isArray(childDirty)) {
              return getDirtyOrDefinedValues(childDirty as any, v as any);
            }
            return v;
          })
          .filter((v) => !isTrulyEmpty(v));

        // Only keep non-empty arrays or those with meaningful items
        if (filtered.length > 0 && !filtered.every(isTrulyEmpty)) {
          result[key] = filtered as T[typeof key];
        }
      } else if (value.length > 0 && !value.every(isTrulyEmpty)) {
        result[key] = value as T[typeof key];
      }
      continue;
    }

    // Objects
    if (value && typeof value === 'object') {
      if (dirty && typeof dirty === 'object' && !Array.isArray(dirty)) {
        const nested = getDirtyOrDefinedValues(dirty as any, value as any);
        if (!isTrulyEmpty(nested)) result[key] = nested as T[typeof key];
      } else if (!isTrulyEmpty(value)) {
        result[key] = value as T[typeof key];
      }
      continue;
    }

    // Primitives (false, 0, null, etc.)
    result[key] = value as T[typeof key];
  }

  // Final cleanup — remove empty nested objects/arrays or fully falsy object arrays
  for (const key of typedKeys(result)) {
    if (isTrulyEmpty(result[key])) delete result[key];
  }

  return result;
}

export const canViewProjectLifecycleForms = (
  project: IProjectView | null,
  user: IUser,
  subscriber?: ISubscriber | null,
  security?: ISecurityGroup | null,
) => {
  return (
    !!subscriber?.canViewProjectLifecycle &&
    ((security?.projectLifecyclePermission !== null &&
      security?.projectLifecyclePermission !== undefined) ||
      user.isSiteAdmin ||
      user.adminOfSubscriberId === project?.subscriberId)
  );
};

// Viewing the lifecycle forms of an archived project is exactly what view-only access is for, so
// only the edit and delete gates below check it -- ahead of the admin fallbacks, since the API
// refuses an admin's writes there too.
export const canEditProjectLifecycleForms = (
  project: IProjectView | null,
  user: IUser,
  subscriber?: ISubscriber | null,
  security?: ISecurityGroup | null,
) => {
  return (
    !!subscriber?.canViewProjectLifecycle &&
    !isProjectArchived(project) &&
    ((!!security?.projectLifecyclePermission && security.projectLifecyclePermission >= 3) ||
      user.isSiteAdmin ||
      user.adminOfSubscriberId === project?.subscriberId)
  );
};

export const canDeleteProjectLifecycleForms = (
  project: IProjectView | null,
  user: IUser,
  subscriber?: ISubscriber | null,
  security?: ISecurityGroup | null,
) => {
  return (
    !!subscriber?.canViewProjectLifecycle &&
    !isProjectArchived(project) &&
    (security?.projectLifecyclePermission === 4 ||
      user.isSiteAdmin ||
      user.adminOfSubscriberId === project?.subscriberId)
  );
};

export const getProjectLifecyleFormsPermission = (
  project: IProjectView | null,
  user: IUser,
  subscriber?: ISubscriber | null,
  security?: ISecurityGroup | null,
) => {
  const canView = canViewProjectLifecycleForms(project, user, subscriber, security);
  if (!canView) {
    return {
      canView: false,
      canEdit: false,
      canDelete: false,
    };
  }
  const canEdit = canEditProjectLifecycleForms(project, user, subscriber, security);
  if (!canEdit) {
    return {
      canView,
      canEdit: false,
      canDelete: false,
    };
  }
  return {
    canView,
    canEdit,
    canDelete: canDeleteProjectLifecycleForms(project, user, subscriber, security),
  };
};

export const getProjectMetrics = ({
  projectLocation,
  startDate,
  projectNumber,
  newSquareFootage,
  renovatedSquareFootage,
  totalSquareFootage,
  deliveryMethod,
  officeLocation,
}: {
  projectLocation?: string;
  startDate?: string;
  projectNumber?: string;
  newSquareFootage?: string;
  renovatedSquareFootage?: string;
  totalSquareFootage?: string;
  deliveryMethod?: string;
  officeLocation?: string;
}) => {
  let str = '<ul>';

  if (projectLocation) str += `<li>Location: ${projectLocation}</li>`;
  if (startDate) str += `<li>Project start date: ${startDate}</li>`;
  if (projectNumber) str += `<li>Grace Project #: ${projectNumber}</li>`;
  if (newSquareFootage && renovatedSquareFootage)
    str += `<li>New Work: ${newSquareFootage} SF | Renovated Work: ${renovatedSquareFootage} SF</li>`;
  else if (totalSquareFootage) str += `<li>Total Work: ${totalSquareFootage} SF</li>`;
  if (deliveryMethod) str += `<li>${deliveryMethod} Project</li>`;
  if (officeLocation) str += `<li>${officeLocation} Studio</li>`;

  str += '</ul>';

  return str;
};

/**
 * The project's users who can reach the Storytelling tab at all -- anyone whose security group
 * grants it any permission level, plus the admins who see every tab regardless of group. This is
 * the pool the storyteller and the Quality Control Form's role fields pick from, so a form never
 * names someone who cannot open the form they are named on.
 */
export const getStorytellingProjectUsers = (
  project: IProjectView | null,
  projectUsers: IProjectUser[],
) => {
  return projectUsers
    .filter(
      (projectUser) =>
        (projectUser.securityGroup?.projectLifecyclePermission !== null &&
          projectUser.securityGroup?.projectLifecyclePermission !== undefined) ||
        projectUser.user?.isSiteAdmin ||
        (!!projectUser.user?.adminOfSubscriberId &&
          projectUser.user.adminOfSubscriberId === project?.subscriberId),
    )
    .sort((a, b) => sortLastNamesDesc(a.user?.name, b.user?.name));
};
